import React, { useMemo, useState } from 'react';
import { useSalesData } from '../hooks/useSalesData';
import { FilterBar } from '../components/FilterBar';
import { MetricCard } from '../components/MetricCard';
import { TrendChart } from '../components/TrendChart';
import { ComparisonTable } from '../components/ComparisonTable';
import { EffortProductionScatter } from '../components/EffortProductionScatter';
import { InsightPanel, Insight } from '../components/InsightPanel';
import { Card } from '../components/ui/Card';
import { FilterState, DailyRecord } from '../types/salesMetrics';
import { filterRecordsByDate, aggregateData, getTotalCalls, getTotalTalkTime, calculateTrend } from '../utils/analytics';
import { format, parseISO, subDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Phone, Target, Trophy, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
export function Dashboard() {
  const {
    data,
    loading,
    teams,
    loanOfficers
  } = useSalesData();
  const [filters, setFilters] = useState<FilterState>({
    dateRange: '30days',
    selectedTeamId: 'all',
    selectedLoId: 'all'
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'effort' | 'production' | 'performance' | 'insights'>('overview');
  // Filter Data
  const filteredData = useMemo(() => {
    let records = filterRecordsByDate(data, filters.dateRange, filters.customStartDate, filters.customEndDate);
    if (filters.selectedTeamId !== 'all') {
      records = records.filter(r => r.teamId === filters.selectedTeamId);
    }
    if (filters.selectedLoId !== 'all') {
      records = records.filter(r => r.loId === filters.selectedLoId);
    }
    return records;
  }, [data, filters]);
  // Aggregate Data
  const aggregated = useMemo(() => aggregateData(filteredData), [filteredData]);
  // Time Series Data for Charts
  const timeSeriesData = useMemo(() => {
    const grouped = new Map();
    filteredData.forEach(record => {
      if (!grouped.has(record.date)) {
        grouped.set(record.date, {
          date: record.date,
          totalCalls: 0,
          dmCalls: 0,
          rehashXfers: 0,
          totalTalkTime: 0,
          applications: 0,
          cbaSent: 0,
          locks: 0,
          stps: 0
        });
      }
      const day = grouped.get(record.date);
      day.totalCalls += getTotalCalls(record.effort);
      day.dmCalls += record.effort.inboundCalls;
      day.rehashXfers += record.effort.rehashCalls;
      day.totalTalkTime += getTotalTalkTime(record.effort);
      day.applications += record.production.applications;
      day.cbaSent += record.production.cbasSent;
      day.locks += record.production.locks;
      day.stps += record.production.stps;
    });
    return Array.from(grouped.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredData]);
  // LO Performance Data for Tables/Scatter
  const loPerformanceData = useMemo(() => {
    const loMap = new Map();
    filteredData.forEach(record => {
      if (!loMap.has(record.loId)) {
        loMap.set(record.loId, {
          id: record.loId,
          name: record.loName,
          team: record.teamName,
          totalCalls: 0,
          totalTalkTime: 0,
          applications: 0,
          locks: 0,
          efficiency: 0
        });
      }
      const lo = loMap.get(record.loId);
      lo.totalCalls += getTotalCalls(record.effort);
      lo.totalTalkTime += getTotalTalkTime(record.effort);
      lo.applications += record.production.applications;
      lo.locks += record.production.locks;
    });
    return Array.from(loMap.values()).map(lo => ({
      ...lo,
      efficiency: lo.totalCalls > 0 ? (lo.applications / lo.totalCalls * 100).toFixed(1) : 0
    }));
  }, [filteredData]);
  // Generate Insights
  const insights: Insight[] = useMemo(() => {
    const list: Insight[] = [];
    // Example logic for insights
    const conversionRate = aggregated.effort.outboundCalls > 0 ? aggregated.production.applications / aggregated.effort.outboundCalls * 100 : 0;
    if (conversionRate < 2) {
      list.push({
        id: 'low-conv',
        type: 'warning',
        title: 'Low Outbound Conversion',
        description: `Outbound call to application conversion is ${conversionRate.toFixed(1)}%, below the 3% target. Consider reviewing call scripts.`,
        metric: '2.0% Conv Rate'
      });
    } else if (conversionRate > 5) {
      list.push({
        id: 'high-conv',
        type: 'positive',
        title: 'High Efficiency Detected',
        description: `Team is converting outbound calls at ${conversionRate.toFixed(1)}%, significantly above average.`,
        metric: 'High Performance'
      });
    }
    if (aggregated.effort.missedCalls > 50) {
      list.push({
        id: 'missed-calls',
        type: 'critical',
        title: 'High Missed Call Volume',
        description: `${aggregated.effort.missedCalls} missed calls detected in this period. Staffing levels may need adjustment during peak hours.`,
        metric: 'Urgent'
      });
    }
    return list;
  }, [aggregated]);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50" data-id="element-138">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" data-id="element-139"></div>
      </div>;
  }
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'effort',
    label: 'Effort Analysis',
    icon: Phone
  }, {
    id: 'production',
    label: 'Production',
    icon: Target
  }, {
    id: 'performance',
    label: 'Rankings',
    icon: Trophy
  }, {
    id: 'insights',
    label: 'Insights',
    icon: AlertCircle
  }];
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-140">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4" data-id="element-141">
        <div className="flex justify-between items-center" data-id="element-142">
          <div data-id="element-143">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-144">
              Sales Effort Analytics
            </h1>
            <p className="text-sm text-slate-500" data-id="element-145">
              Real-time performance monitoring and insights
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-146">
            <div className="text-right hidden sm:block" data-id="element-147">
              <div className="text-sm font-medium text-slate-900" data-id="element-148">
                Welcome back, Alex
              </div>
              <div className="text-xs text-slate-500" data-id="element-149">Sales Manager</div>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold" data-id="element-150">
              AM
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <FilterBar filters={filters} onFilterChange={setFilters} teams={teams} loanOfficers={loanOfficers} data-id="element-151" />

      {/* Navigation Tabs */}
      <div className="px-6 pt-6 pb-0 border-b border-slate-200 bg-white sticky top-[88px] z-10" data-id="element-152">
        <div className="flex gap-6 overflow-x-auto no-scrollbar" data-id="element-153">
          {tabs.map(tab => {
          const Icon = tab.icon;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={clsx('flex items-center gap-2 pb-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap', activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300')} data-id="element-154">
                <Icon className="w-4 h-4" data-id="element-155" />
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6 max-w-[1600px] mx-auto" data-id="element-156">
        <AnimatePresence mode="wait" data-id="element-157">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-158">
            {activeTab === 'overview' && <div className="space-y-6" data-id="element-159">
                {/* Key Metrics Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-160">
                  <MetricCard title="Total Calls" value={getTotalCalls(aggregated.effort).toLocaleString()} subValue={`${Math.round(getTotalCalls(aggregated.effort) / aggregated.recordCount || 0)} avg/day`} trend={{
                value: 12,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="totalCalls" color="blue" delay={0} data-id="element-161" />
                  <MetricCard title="Talk Time (Hours)" value={Math.round(getTotalTalkTime(aggregated.effort) / 60).toLocaleString()} subValue={`${Math.round(getTotalTalkTime(aggregated.effort) / 60 / aggregated.recordCount || 0)} hrs/day`} trend={{
                value: 5,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="totalTalkTime" color="indigo" delay={0.1} data-id="element-162" />
                  <MetricCard title="Applications" value={aggregated.production.applications.toLocaleString()} subValue={`${(aggregated.production.applications / (getTotalCalls(aggregated.effort) || 1) * 100).toFixed(1)}% conversion`} trend={{
                value: 2,
                direction: 'down',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="applications" color="green" delay={0.2} data-id="element-163" />
                  <MetricCard title="Locks" value={aggregated.production.locks.toLocaleString()} subValue={`${(aggregated.production.locks / (aggregated.production.applications || 1) * 100).toFixed(1)}% pull-through`} trend={{
                value: 8,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="locks" color="rose" delay={0.3} data-id="element-164" />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[400px]" data-id="element-165">
                  <div className="lg:col-span-2 h-full" data-id="element-166">
                    <TrendChart title="Effort vs Production Trend" description="Call volume breakdown (DM Calls + Rehash Xfers) and production pipeline metrics over time." data={timeSeriesData} metrics={[{
                  key: 'dmCalls',
                  name: 'Direct Mail Calls',
                  color: '#3b82f6',
                  type: 'bar',
                  stackId: 'calls'
                }, {
                  key: 'rehashXfers',
                  name: 'Rehash Xfers',
                  color: '#8b5cf6',
                  type: 'bar',
                  stackId: 'calls'
                }, {
                  key: 'applications',
                  name: 'Apps',
                  color: '#10b981',
                  type: 'line'
                }, {
                  key: 'cbaSent',
                  name: 'CBA Sent',
                  color: '#059669',
                  type: 'line'
                }, {
                  key: 'locks',
                  name: 'Locks',
                  color: '#047857',
                  type: 'line'
                }, {
                  key: 'stps',
                  name: 'STP',
                  color: '#065f46',
                  type: 'line'
                }]} data-id="element-167" />
                  </div>
                  <div className="h-full" data-id="element-168">
                    <InsightPanel insights={insights} data-id="element-169" />
                  </div>
                </div>

                {/* Bottom Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-170">
                  <ComparisonTable title="Top Performers (Efficiency)" data={loPerformanceData.slice(0, 5)} columns={[{
                key: 'name',
                label: 'Loan Officer'
              }, {
                key: 'team',
                label: 'Team'
              }, {
                key: 'totalCalls',
                label: 'Calls',
                align: 'right',
                sortable: true
              }, {
                key: 'applications',
                label: 'Apps',
                align: 'right',
                sortable: true
              }, {
                key: 'efficiency',
                label: 'Conv %',
                align: 'right',
                sortable: true,
                format: v => <span className="font-bold text-emerald-600" data-id="element-172">
                            {v}%
                          </span>
              }]} data-id="element-171" />
                  <div className="h-[400px]" data-id="element-173">
                    <EffortProductionScatter data={loPerformanceData} xAxisKey="totalCalls" yAxisKey="applications" xAxisName="Total Calls" yAxisName="Applications" data-id="element-174" />
                  </div>
                </div>
              </div>}

            {activeTab === 'effort' && <div className="space-y-6" data-id="element-175">
                {/* Effort Metrics Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-176">
                  <MetricCard title="Total Calls" value={getTotalCalls(aggregated.effort).toLocaleString()} subValue={`${Math.round(getTotalCalls(aggregated.effort) / aggregated.recordCount || 0)} per day`} trend={{
                value: 12,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="totalCalls" color="blue" delay={0} data-id="element-177" />
                  <MetricCard title="Talk Time" value={`${Math.round(getTotalTalkTime(aggregated.effort) / 60)}h`} subValue={`${Math.round(getTotalTalkTime(aggregated.effort) / getTotalCalls(aggregated.effort) || 0)} min/call avg`} trend={{
                value: 5,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="totalTalkTime" color="indigo" delay={0.1} data-id="element-178" />
                  <MetricCard title="SMS Sent" value={aggregated.effort.smsSent.toLocaleString()} subValue={`${Math.round(aggregated.effort.smsSent / aggregated.recordCount || 0)} per day`} trend={{
                value: 8,
                direction: 'up',
                label: 'vs last period'
              }} color="blue" delay={0.2} data-id="element-179" />
                  <MetricCard title="Friction Signals" value={(aggregated.effort.callsRejected + aggregated.effort.missedCalls + aggregated.effort.declinedTransfers).toLocaleString()} subValue="Rejected + Missed + Declined" trend={{
                value: 3,
                direction: 'down',
                label: 'vs last period'
              }} color="rose" delay={0.3} data-id="element-180" />
                </div>

                {/* Call Mix & Talk Time Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-181">
                  {/* Call Mix Breakdown */}
                  <Card data-id="element-182">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-183">
                      Call Mix Breakdown
                    </h3>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-184">
                      Distribution of call types across the selected period
                    </p>

                    <div className="space-y-4" data-id="element-185">
                      {/* Inbound */}
                      <div data-id="element-186">
                        <div className="flex justify-between items-center mb-2" data-id="element-187">
                          <span className="text-sm font-medium text-slate-700" data-id="element-188">
                            Inbound Calls
                          </span>
                          <span className="text-sm font-bold text-slate-900" data-id="element-189">
                            {aggregated.effort.inboundCalls.toLocaleString()} (
                            {(aggregated.effort.inboundCalls / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            %)
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-190">
                          <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{
                        width: `${aggregated.effort.inboundCalls / getTotalCalls(aggregated.effort) * 100}%`
                      }} data-id="element-191"></div>
                        </div>
                        <div className="flex justify-between items-center mt-1" data-id="element-192">
                          <span className="text-xs text-slate-500" data-id="element-193">
                            Talk Time:{' '}
                            {Math.round(aggregated.effort.inboundTalkTime / 60)}
                            h
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-194">
                            Avg:{' '}
                            {Math.round(aggregated.effort.inboundTalkTime / aggregated.effort.inboundCalls || 0)}{' '}
                            min
                          </span>
                        </div>
                      </div>

                      {/* Outbound */}
                      <div data-id="element-195">
                        <div className="flex justify-between items-center mb-2" data-id="element-196">
                          <span className="text-sm font-medium text-slate-700" data-id="element-197">
                            Outbound Calls
                          </span>
                          <span className="text-sm font-bold text-slate-900" data-id="element-198">
                            {aggregated.effort.outboundCalls.toLocaleString()} (
                            {(aggregated.effort.outboundCalls / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            %)
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-199">
                          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                        width: `${aggregated.effort.outboundCalls / getTotalCalls(aggregated.effort) * 100}%`
                      }} data-id="element-200"></div>
                        </div>
                        <div className="flex justify-between items-center mt-1" data-id="element-201">
                          <span className="text-xs text-slate-500" data-id="element-202">
                            Talk Time:{' '}
                            {Math.round(aggregated.effort.outboundTalkTime / 60)}
                            h
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-203">
                            Avg:{' '}
                            {Math.round(aggregated.effort.outboundTalkTime / aggregated.effort.outboundCalls || 0)}{' '}
                            min
                          </span>
                        </div>
                      </div>

                      {/* Rehash */}
                      <div data-id="element-204">
                        <div className="flex justify-between items-center mb-2" data-id="element-205">
                          <span className="text-sm font-medium text-slate-700" data-id="element-206">
                            Rehash Calls
                          </span>
                          <span className="text-sm font-bold text-slate-900" data-id="element-207">
                            {aggregated.effort.rehashCalls.toLocaleString()} (
                            {(aggregated.effort.rehashCalls / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            %)
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-208">
                          <div className="h-full bg-violet-500 rounded-full transition-all duration-500" style={{
                        width: `${aggregated.effort.rehashCalls / getTotalCalls(aggregated.effort) * 100}%`
                      }} data-id="element-209"></div>
                        </div>
                        <div className="flex justify-between items-center mt-1" data-id="element-210">
                          <span className="text-xs text-slate-500" data-id="element-211">
                            Talk Time:{' '}
                            {Math.round(aggregated.effort.rehashTalkTime / 60)}h
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-212">
                            Avg:{' '}
                            {Math.round(aggregated.effort.rehashTalkTime / aggregated.effort.rehashCalls || 0)}{' '}
                            min
                          </span>
                        </div>
                      </div>

                      {/* Personal Line */}
                      <div data-id="element-213">
                        <div className="flex justify-between items-center mb-2" data-id="element-214">
                          <span className="text-sm font-medium text-slate-700" data-id="element-215">
                            Personal Line Calls
                          </span>
                          <span className="text-sm font-bold text-slate-900" data-id="element-216">
                            {aggregated.effort.personalLineCalls.toLocaleString()}{' '}
                            (
                            {(aggregated.effort.personalLineCalls / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            %)
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-217">
                          <div className="h-full bg-cyan-500 rounded-full transition-all duration-500" style={{
                        width: `${aggregated.effort.personalLineCalls / getTotalCalls(aggregated.effort) * 100}%`
                      }} data-id="element-218"></div>
                        </div>
                        <div className="flex justify-between items-center mt-1" data-id="element-219">
                          <span className="text-xs text-slate-500" data-id="element-220">
                            Talk Time:{' '}
                            {Math.round(aggregated.effort.personalLineTalkTime / 60)}
                            h
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-221">
                            Avg:{' '}
                            {Math.round(aggregated.effort.personalLineTalkTime / aggregated.effort.personalLineCalls || 0)}{' '}
                            min
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-222">
                      <div className="flex justify-between items-center" data-id="element-223">
                        <span className="text-sm font-medium text-slate-700" data-id="element-224">
                          Total Talk Time
                        </span>
                        <span className="text-xl font-bold text-indigo-600" data-id="element-225">
                          {Math.round(getTotalTalkTime(aggregated.effort) / 60)}{' '}
                          hours
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Talk Time Efficiency */}
                  <Card data-id="element-226">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-227">
                      Talk Time Efficiency
                    </h3>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-228">
                      Average talk time per call type and conversion correlation
                    </p>

                    <div className="space-y-6" data-id="element-229">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-230">
                        <div className="flex justify-between items-center mb-2" data-id="element-231">
                          <span className="text-sm font-medium text-slate-700" data-id="element-232">
                            Avg Call Duration
                          </span>
                          <span className="text-2xl font-bold text-blue-600" data-id="element-233">
                            {Math.round(getTotalTalkTime(aggregated.effort) / getTotalCalls(aggregated.effort) || 0)}{' '}
                            min
                          </span>
                        </div>
                        <p className="text-xs text-slate-600" data-id="element-234">
                          Across all{' '}
                          {getTotalCalls(aggregated.effort).toLocaleString()}{' '}
                          calls
                        </p>
                      </div>

                      <div data-id="element-235">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-236">
                          Talk Time by Channel
                        </h4>
                        <div className="space-y-3" data-id="element-237">
                          <div className="flex items-center justify-between" data-id="element-238">
                            <span className="text-sm text-slate-600" data-id="element-239">
                              Inbound
                            </span>
                            <div className="flex items-center gap-2" data-id="element-240">
                              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-241">
                                <div className="h-full bg-blue-500 rounded-full" style={{
                              width: '75%'
                            }} data-id="element-242"></div>
                              </div>
                              <span className="text-sm font-medium text-slate-900 w-12 text-right" data-id="element-243">
                                {Math.round(aggregated.effort.inboundTalkTime / aggregated.effort.inboundCalls || 0)}
                                m
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-244">
                            <span className="text-sm text-slate-600" data-id="element-245">
                              Outbound
                            </span>
                            <div className="flex items-center gap-2" data-id="element-246">
                              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-247">
                                <div className="h-full bg-indigo-500 rounded-full" style={{
                              width: '60%'
                            }} data-id="element-248"></div>
                              </div>
                              <span className="text-sm font-medium text-slate-900 w-12 text-right" data-id="element-249">
                                {Math.round(aggregated.effort.outboundTalkTime / aggregated.effort.outboundCalls || 0)}
                                m
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-250">
                            <span className="text-sm text-slate-600" data-id="element-251">
                              Rehash
                            </span>
                            <div className="flex items-center gap-2" data-id="element-252">
                              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-253">
                                <div className="h-full bg-violet-500 rounded-full" style={{
                              width: '45%'
                            }} data-id="element-254"></div>
                              </div>
                              <span className="text-sm font-medium text-slate-900 w-12 text-right" data-id="element-255">
                                {Math.round(aggregated.effort.rehashTalkTime / aggregated.effort.rehashCalls || 0)}
                                m
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-256">
                            <span className="text-sm text-slate-600" data-id="element-257">
                              Personal
                            </span>
                            <div className="flex items-center gap-2" data-id="element-258">
                              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-259">
                                <div className="h-full bg-cyan-500 rounded-full" style={{
                              width: '50%'
                            }} data-id="element-260"></div>
                              </div>
                              <span className="text-sm font-medium text-slate-900 w-12 text-right" data-id="element-261">
                                {Math.round(aggregated.effort.personalLineTalkTime / aggregated.effort.personalLineCalls || 0)}
                                m
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-262">
                        <div className="flex justify-between items-center" data-id="element-263">
                          <div data-id="element-264">
                            <div className="text-sm font-medium text-slate-700" data-id="element-265">
                              Talk Time → Apps Ratio
                            </div>
                            <div className="text-xs text-slate-600 mt-1" data-id="element-266">
                              Minutes per application generated
                            </div>
                          </div>
                          <div className="text-right" data-id="element-267">
                            <div className="text-2xl font-bold text-emerald-600" data-id="element-268">
                              {Math.round(getTotalTalkTime(aggregated.effort) / aggregated.production.applications || 0)}
                            </div>
                            <div className="text-xs text-slate-600" data-id="element-269">
                              min/app
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Friction Analysis & Digital Outreach */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-270">
                  {/* Friction Signals */}
                  <Card data-id="element-271">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-272">
                      <span className="text-red-600" data-id="element-273">⚠️</span>
                      Friction Signals
                    </h3>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-274">
                      Resistance indicators that may signal process issues
                    </p>

                    <div className="space-y-4" data-id="element-275">
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200" data-id="element-276">
                        <div className="flex justify-between items-center mb-2" data-id="element-277">
                          <span className="text-sm font-medium text-slate-700" data-id="element-278">
                            Calls Rejected
                          </span>
                          <span className="text-2xl font-bold text-red-600" data-id="element-279">
                            {aggregated.effort.callsRejected.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-slate-600" data-id="element-280">
                          <span data-id="element-281">Prospects actively declining calls</span>
                          <span data-id="element-282">
                            {(aggregated.effort.callsRejected / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            % of total
                          </span>
                        </div>
                      </div>

                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-id="element-283">
                        <div className="flex justify-between items-center mb-2" data-id="element-284">
                          <span className="text-sm font-medium text-slate-700" data-id="element-285">
                            Missed Calls
                          </span>
                          <span className="text-2xl font-bold text-amber-600" data-id="element-286">
                            {aggregated.effort.missedCalls.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-slate-600" data-id="element-287">
                          <span data-id="element-288">Calls not answered by LOs</span>
                          <span data-id="element-289">
                            {(aggregated.effort.missedCalls / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            % of total
                          </span>
                        </div>
                      </div>

                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200" data-id="element-290">
                        <div className="flex justify-between items-center mb-2" data-id="element-291">
                          <span className="text-sm font-medium text-slate-700" data-id="element-292">
                            Declined Transfers
                          </span>
                          <span className="text-2xl font-bold text-orange-600" data-id="element-293">
                            {aggregated.effort.declinedTransfers.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-slate-600" data-id="element-294">
                          <span data-id="element-295">Transfers refused by prospects</span>
                          <span data-id="element-296">
                            {(aggregated.effort.declinedTransfers / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            % of total
                          </span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-200" data-id="element-297">
                        <div className="flex justify-between items-center" data-id="element-298">
                          <span className="text-sm font-medium text-slate-700" data-id="element-299">
                            Total Friction Rate
                          </span>
                          <span className="text-xl font-bold text-slate-900" data-id="element-300">
                            {((aggregated.effort.callsRejected + aggregated.effort.missedCalls + aggregated.effort.declinedTransfers) / getTotalCalls(aggregated.effort) * 100).toFixed(1)}
                            %
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-301">
                          Target: Keep below 5% for healthy pipeline
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Digital Outreach */}
                  <Card data-id="element-302">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-303">
                      <span className="text-blue-600" data-id="element-304">📱</span>
                      Digital Outreach
                    </h3>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-305">
                      SMS and digital engagement metrics
                    </p>

                    <div className="space-y-6" data-id="element-306">
                      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200" data-id="element-307">
                        <div className="text-center" data-id="element-308">
                          <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-309">
                            {aggregated.effort.smsSent.toLocaleString()}
                          </div>
                          <div className="text-sm text-slate-600 mb-4" data-id="element-310">
                            Total SMS Sent
                          </div>
                          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-blue-200" data-id="element-311">
                            <div data-id="element-312">
                              <div className="text-xl font-bold text-slate-900" data-id="element-313">
                                {Math.round(aggregated.effort.smsSent / aggregated.recordCount || 0)}
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-314">
                                Per Day
                              </div>
                            </div>
                            <div data-id="element-315">
                              <div className="text-xl font-bold text-slate-900" data-id="element-316">
                                {(aggregated.effort.smsSent / getTotalCalls(aggregated.effort)).toFixed(1)}
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-317">
                                Per Call
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-318">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-319">
                          Digital Engagement Strategy
                        </h4>
                        <div className="space-y-2 text-sm text-slate-600" data-id="element-320">
                          <div className="flex items-start gap-2" data-id="element-321">
                            <span className="text-green-600 mt-0.5" data-id="element-322">✓</span>
                            <span data-id="element-323">
                              SMS used as follow-up to{' '}
                              {(aggregated.effort.smsSent / getTotalCalls(aggregated.effort) * 100).toFixed(0)}
                              % of calls
                            </span>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-324">
                            <span className="text-blue-600 mt-0.5" data-id="element-325">→</span>
                            <span data-id="element-326">
                              Average{' '}
                              {(aggregated.effort.smsSent / loPerformanceData.length).toFixed(0)}{' '}
                              SMS per LO
                            </span>
                          </div>
                          <div className="flex items-start gap-2" data-id="element-327">
                            <span className="text-indigo-600 mt-0.5" data-id="element-328">📊</span>
                            <span data-id="element-329">
                              Multi-channel approach combining voice + text
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200" data-id="element-330">
                        <div className="text-sm font-medium text-slate-700 mb-1" data-id="element-331">
                          SMS → Application Rate
                        </div>
                        <div className="text-2xl font-bold text-emerald-600" data-id="element-332">
                          {(aggregated.production.applications / aggregated.effort.smsSent * 100).toFixed(1)}
                          %
                        </div>
                        <p className="text-xs text-slate-600 mt-1" data-id="element-333">
                          Applications per SMS sent (correlation metric)
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Activity Trend */}
                <TrendChart title="Activity Trend Over Time" description="Daily call volume breakdown (Direct Mail + Rehash) and production metrics across the selected period." data={timeSeriesData} metrics={[{
              key: 'dmCalls',
              name: 'Direct Mail Calls',
              color: '#3b82f6',
              type: 'bar',
              stackId: 'calls'
            }, {
              key: 'rehashXfers',
              name: 'Rehash Xfers',
              color: '#8b5cf6',
              type: 'bar',
              stackId: 'calls'
            }, {
              key: 'applications',
              name: 'Apps',
              color: '#10b981',
              type: 'line'
            }, {
              key: 'cbaSent',
              name: 'CBA Sent',
              color: '#059669',
              type: 'line'
            }, {
              key: 'locks',
              name: 'Locks',
              color: '#047857',
              type: 'line'
            }, {
              key: 'stps',
              name: 'STP',
              color: '#065f46',
              type: 'line'
            }]} height={350} data-id="element-334" />
              </div>}

            {activeTab === 'production' && <div className="space-y-6" data-id="element-335">
                {/* Production Metrics Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-336">
                  <MetricCard title="Applications" value={aggregated.production.applications.toLocaleString()} subValue={`${Math.round(aggregated.production.applications / aggregated.recordCount || 0)} per day`} trend={{
                value: 8,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="applications" color="green" delay={0} data-id="element-337" />
                  <MetricCard title="CBAs Sent" value={aggregated.production.cbasSent.toLocaleString()} subValue={`${(aggregated.production.cbasSent / (aggregated.production.applications || 1) * 100).toFixed(0)}% from apps`} trend={{
                value: 5,
                direction: 'up',
                label: 'vs last period'
              }} color="green" delay={0.1} data-id="element-338" />
                  <MetricCard title="Locks" value={aggregated.production.locks.toLocaleString()} subValue={`${(aggregated.production.locks / (aggregated.production.cbasSent || 1) * 100).toFixed(0)}% from CBAs`} trend={{
                value: 12,
                direction: 'up',
                label: 'vs last period'
              }} data={timeSeriesData} dataKey="locks" color="green" delay={0.2} data-id="element-339" />
                  <MetricCard title="STPs" value={aggregated.production.stps.toLocaleString()} subValue={`${(aggregated.production.stps / (aggregated.production.locks || 1) * 100).toFixed(0)}% from locks`} trend={{
                value: 3,
                direction: 'down',
                label: 'vs last period'
              }} color="green" delay={0.3} data-id="element-340" />
                </div>

                {/* Conversion Funnel & Rates */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-341">
                  {/* Funnel Visualization */}
                  <div className="lg:col-span-2" data-id="element-342">
                    <Card className="h-full" data-id="element-343">
                      <div className="flex justify-between items-start mb-6" data-id="element-344">
                        <div data-id="element-345">
                          <h3 className="text-lg font-semibold text-slate-900" data-id="element-346">
                            Conversion Funnel
                          </h3>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-347">
                            Pipeline progression from applications to
                            submit-to-processing
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-348">
                        {/* Applications */}
                        <div className="relative" data-id="element-349">
                          <div className="flex items-center justify-between mb-2" data-id="element-350">
                            <span className="text-sm font-medium text-slate-700" data-id="element-351">
                              Applications
                            </span>
                            <span className="text-sm font-bold text-slate-900" data-id="element-352">
                              {aggregated.production.applications.toLocaleString()}
                            </span>
                          </div>
                          <div className="h-12 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-lg shadow-sm flex items-center justify-center" data-id="element-353">
                            <span className="text-white font-semibold text-sm" data-id="element-354">
                              100%
                            </span>
                          </div>
                        </div>

                        {/* CBAs */}
                        <div className="relative pl-8" data-id="element-355">
                          <div className="flex items-center justify-between mb-2" data-id="element-356">
                            <span className="text-sm font-medium text-slate-700" data-id="element-357">
                              CBAs Sent
                            </span>
                            <div className="flex items-center gap-3" data-id="element-358">
                              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-359">
                                {(aggregated.production.cbasSent / (aggregated.production.applications || 1) * 100).toFixed(1)}
                                % conversion
                              </span>
                              <span className="text-sm font-bold text-slate-900" data-id="element-360">
                                {aggregated.production.cbasSent.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="h-12 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-lg shadow-sm flex items-center justify-center" style={{
                        width: `${aggregated.production.cbasSent / (aggregated.production.applications || 1) * 100}%`
                      }} data-id="element-361">
                            <span className="text-white font-semibold text-sm" data-id="element-362">
                              {(aggregated.production.cbasSent / (aggregated.production.applications || 1) * 100).toFixed(0)}
                              %
                            </span>
                          </div>
                        </div>

                        {/* Locks */}
                        <div className="relative pl-16" data-id="element-363">
                          <div className="flex items-center justify-between mb-2" data-id="element-364">
                            <span className="text-sm font-medium text-slate-700" data-id="element-365">
                              Locks
                            </span>
                            <div className="flex items-center gap-3" data-id="element-366">
                              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-367">
                                {(aggregated.production.locks / (aggregated.production.cbasSent || 1) * 100).toFixed(1)}
                                % conversion
                              </span>
                              <span className="text-sm font-bold text-slate-900" data-id="element-368">
                                {aggregated.production.locks.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="h-12 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-lg shadow-sm flex items-center justify-center" style={{
                        width: `${aggregated.production.locks / (aggregated.production.applications || 1) * 100}%`
                      }} data-id="element-369">
                            <span className="text-white font-semibold text-sm" data-id="element-370">
                              {(aggregated.production.locks / (aggregated.production.applications || 1) * 100).toFixed(0)}
                              %
                            </span>
                          </div>
                        </div>

                        {/* STPs */}
                        <div className="relative pl-24" data-id="element-371">
                          <div className="flex items-center justify-between mb-2" data-id="element-372">
                            <span className="text-sm font-medium text-slate-700" data-id="element-373">
                              Submit to Processing
                            </span>
                            <div className="flex items-center gap-3" data-id="element-374">
                              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-375">
                                {(aggregated.production.stps / (aggregated.production.locks || 1) * 100).toFixed(1)}
                                % conversion
                              </span>
                              <span className="text-sm font-bold text-slate-900" data-id="element-376">
                                {aggregated.production.stps.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="h-12 bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-lg shadow-sm flex items-center justify-center" style={{
                        width: `${aggregated.production.stps / (aggregated.production.applications || 1) * 100}%`
                      }} data-id="element-377">
                            <span className="text-white font-semibold text-sm" data-id="element-378">
                              {(aggregated.production.stps / (aggregated.production.applications || 1) * 100).toFixed(0)}
                              %
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Overall Pull-Through */}
                      <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-379">
                        <div className="flex items-center justify-between" data-id="element-380">
                          <div data-id="element-381">
                            <p className="text-sm font-medium text-slate-700" data-id="element-382">
                              Overall Pull-Through Rate
                            </p>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-383">
                              Applications → STPs
                            </p>
                          </div>
                          <div className="text-right" data-id="element-384">
                            <p className="text-2xl font-bold text-emerald-600" data-id="element-385">
                              {(aggregated.production.stps / (aggregated.production.applications || 1) * 100).toFixed(1)}
                              %
                            </p>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-386">
                              Industry avg: 35-45%
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Conversion Rate Breakdown */}
                  <div data-id="element-387">
                    <Card className="h-full" data-id="element-388">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-389">
                        Stage Conversion Rates
                      </h3>
                      <div className="space-y-4" data-id="element-390">
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-391">
                          <div className="flex items-center justify-between mb-1" data-id="element-392">
                            <span className="text-sm font-medium text-slate-700" data-id="element-393">
                              App → CBA
                            </span>
                            <span className="text-lg font-bold text-emerald-700" data-id="element-394">
                              {(aggregated.production.cbasSent / (aggregated.production.applications || 1) * 100).toFixed(1)}
                              %
                            </span>
                          </div>
                          <p className="text-xs text-slate-600" data-id="element-395">
                            Target: 75-85%
                          </p>
                        </div>

                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-396">
                          <div className="flex items-center justify-between mb-1" data-id="element-397">
                            <span className="text-sm font-medium text-slate-700" data-id="element-398">
                              CBA → Lock
                            </span>
                            <span className="text-lg font-bold text-emerald-700" data-id="element-399">
                              {(aggregated.production.locks / (aggregated.production.cbasSent || 1) * 100).toFixed(1)}
                              %
                            </span>
                          </div>
                          <p className="text-xs text-slate-600" data-id="element-400">
                            Target: 55-65%
                          </p>
                        </div>

                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-401">
                          <div className="flex items-center justify-between mb-1" data-id="element-402">
                            <span className="text-sm font-medium text-slate-700" data-id="element-403">
                              Lock → STP
                            </span>
                            <span className="text-lg font-bold text-emerald-700" data-id="element-404">
                              {(aggregated.production.stps / (aggregated.production.locks || 1) * 100).toFixed(1)}
                              %
                            </span>
                          </div>
                          <p className="text-xs text-slate-600" data-id="element-405">
                            Target: 85-95%
                          </p>
                        </div>

                        <div className="pt-4 border-t border-slate-200" data-id="element-406">
                          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-407">
                            <div className="flex items-center justify-between mb-1" data-id="element-408">
                              <span className="text-sm font-medium text-slate-700" data-id="element-409">
                                End-to-End
                              </span>
                              <span className="text-lg font-bold text-slate-900" data-id="element-410">
                                {(aggregated.production.stps / (aggregated.production.applications || 1) * 100).toFixed(1)}
                                %
                              </span>
                            </div>
                            <p className="text-xs text-slate-600" data-id="element-411">
                              Target: 35-45%
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Production Trends & Top Producers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-412">
                  <TrendChart title="Production Trend Over Time" description="Daily production volume across all stages of the pipeline." data={timeSeriesData} metrics={[{
                key: 'applications',
                name: 'Applications',
                color: '#10b981',
                type: 'line'
              }, {
                key: 'locks',
                name: 'Locks',
                color: '#059669',
                type: 'line'
              }]} height={350} data-id="element-413" />

                  <ComparisonTable title="Top Producers (Volume)" data={loPerformanceData.sort((a, b) => b.applications - a.applications).slice(0, 8)} columns={[{
                key: 'name',
                label: 'Loan Officer'
              }, {
                key: 'applications',
                label: 'Apps',
                align: 'right',
                sortable: true
              }, {
                key: 'locks',
                label: 'Locks',
                align: 'right',
                sortable: true
              }, {
                key: 'locks',
                label: 'Pull-Through',
                align: 'right',
                format: (v, row) => {
                  const rate = row.applications > 0 ? row.locks / row.applications * 100 : 0;
                  return <span className={rate > 40 ? 'font-bold text-emerald-600' : 'text-slate-600'} data-id="element-415">
                              {rate.toFixed(0)}%
                            </span>;
                }
              }]} data-id="element-414" />
                </div>
              </div>}

            {activeTab === 'performance' && <div className="space-y-6" data-id="element-416">
                {/* Top 3 Podium */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-417">
                  {loPerformanceData.sort((a, b) => b.applications - a.applications).slice(0, 3).map((lo, idx) => {
                const medals = ['🥇', '🥈', '🥉'];
                const colors = ['from-yellow-400 to-yellow-500', 'from-slate-300 to-slate-400', 'from-amber-600 to-amber-700'];
                const textColors = ['text-yellow-600', 'text-slate-600', 'text-amber-700'];
                const bgColors = ['bg-yellow-50', 'bg-slate-50', 'bg-amber-50'];
                return <motion.div key={lo.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  delay: idx * 0.1
                }} data-id="element-418">
                          <Card className={`${bgColors[idx]} border-2 ${idx === 0 ? 'border-yellow-300' : idx === 1 ? 'border-slate-300' : 'border-amber-300'}`} data-id="element-419">
                            <div className="text-center" data-id="element-420">
                              <div className="text-5xl mb-2" data-id="element-421">{medals[idx]}</div>
                              <div className={`text-xs font-bold ${textColors[idx]} uppercase tracking-wider mb-2`} data-id="element-422">
                                Rank #{idx + 1}
                              </div>
                              <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-423">
                                {lo.name}
                              </h3>
                              <p className="text-sm text-slate-600 mb-4" data-id="element-424">
                                {lo.team}
                              </p>

                              <div className="space-y-3" data-id="element-425">
                                <div className={`p-3 ${bgColors[idx]} rounded-lg border ${idx === 0 ? 'border-yellow-200' : idx === 1 ? 'border-slate-200' : 'border-amber-200'}`} data-id="element-426">
                                  <div className="text-2xl font-bold text-slate-900" data-id="element-427">
                                    {lo.applications}
                                  </div>
                                  <div className="text-xs text-slate-600" data-id="element-428">
                                    Applications
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-429">
                                  <div data-id="element-430">
                                    <div className="font-bold text-slate-900" data-id="element-431">
                                      {lo.locks}
                                    </div>
                                    <div className="text-xs text-slate-600" data-id="element-432">
                                      Locks
                                    </div>
                                  </div>
                                  <div data-id="element-433">
                                    <div className="font-bold text-emerald-600" data-id="element-434">
                                      {lo.efficiency}%
                                    </div>
                                    <div className="text-xs text-slate-600" data-id="element-435">
                                      Efficiency
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </motion.div>;
              })}
                </div>

                {/* Ranking Categories */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-436">
                  {/* By Volume */}
                  <ComparisonTable title="Rankings by Volume" data={loPerformanceData.sort((a, b) => b.applications - a.applications)} columns={[{
                key: 'name',
                label: 'Loan Officer',
                format: (v, row) => {
                  const rank = loPerformanceData.sort((a, b) => b.applications - a.applications).findIndex(lo => lo.id === row.id) + 1;
                  return <div className="flex items-center gap-3" data-id="element-438">
                              <div className={clsx('flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold', rank === 1 && 'bg-yellow-100 text-yellow-700', rank === 2 && 'bg-slate-100 text-slate-700', rank === 3 && 'bg-amber-100 text-amber-700', rank > 3 && 'bg-slate-50 text-slate-500')} data-id="element-439">
                                {rank}
                              </div>
                              <span className="font-medium" data-id="element-440">{v}</span>
                            </div>;
                }
              }, {
                key: 'totalCalls',
                label: 'Calls',
                align: 'right',
                sortable: true
              }, {
                key: 'applications',
                label: 'Apps',
                align: 'right',
                sortable: true,
                format: v => <span className="font-bold text-slate-900" data-id="element-441">{v}</span>
              }, {
                key: 'locks',
                label: 'Locks',
                align: 'right',
                sortable: true
              }]} data-id="element-437" />

                  {/* By Efficiency */}
                  <ComparisonTable title="Rankings by Efficiency" data={loPerformanceData.sort((a, b) => parseFloat(b.efficiency) - parseFloat(a.efficiency))} columns={[{
                key: 'name',
                label: 'Loan Officer',
                format: (v, row) => {
                  const rank = loPerformanceData.sort((a, b) => parseFloat(b.efficiency) - parseFloat(a.efficiency)).findIndex(lo => lo.id === row.id) + 1;
                  return <div className="flex items-center gap-3" data-id="element-443">
                              <div className={clsx('flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold', rank === 1 && 'bg-yellow-100 text-yellow-700', rank === 2 && 'bg-slate-100 text-slate-700', rank === 3 && 'bg-amber-100 text-amber-700', rank > 3 && 'bg-slate-50 text-slate-500')} data-id="element-444">
                                {rank}
                              </div>
                              <span className="font-medium" data-id="element-445">{v}</span>
                            </div>;
                }
              }, {
                key: 'efficiency',
                label: 'Conv %',
                align: 'right',
                sortable: true,
                format: v => <span className="font-bold text-emerald-600" data-id="element-446">
                            {v}%
                          </span>
              }, {
                key: 'applications',
                label: 'Apps',
                align: 'right',
                sortable: true
              }, {
                key: 'totalCalls',
                label: 'Calls',
                align: 'right',
                sortable: true
              }]} data-id="element-442" />
                </div>

                {/* Team Performance Comparison */}
                <Card data-id="element-447">
                  <div className="flex justify-between items-start mb-6" data-id="element-448">
                    <div data-id="element-449">
                      <h3 className="text-lg font-semibold text-slate-900" data-id="element-450">
                        Team Performance Comparison
                      </h3>
                      <p className="text-sm text-slate-500 mt-1" data-id="element-451">
                        Average metrics by team
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-452">
                    {teams.map((team, idx) => {
                  const teamLos = loPerformanceData.filter(lo => lo.team === team.name);
                  const avgCalls = teamLos.reduce((sum, lo) => sum + lo.totalCalls, 0) / teamLos.length;
                  const avgApps = teamLos.reduce((sum, lo) => sum + lo.applications, 0) / teamLos.length;
                  const avgEfficiency = teamLos.reduce((sum, lo) => sum + parseFloat(lo.efficiency), 0) / teamLos.length;
                  return <motion.div key={team.id} initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    duration: 0.3,
                    delay: idx * 0.1
                  }} className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-100" data-id="element-453">
                          <h4 className="font-semibold text-slate-900 mb-3" data-id="element-454">
                            {team.name}
                          </h4>
                          <div className="space-y-2" data-id="element-455">
                            <div className="flex justify-between items-center" data-id="element-456">
                              <span className="text-sm text-slate-600" data-id="element-457">
                                Avg Calls
                              </span>
                              <span className="font-bold text-slate-900" data-id="element-458">
                                {Math.round(avgCalls)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center" data-id="element-459">
                              <span className="text-sm text-slate-600" data-id="element-460">
                                Avg Apps
                              </span>
                              <span className="font-bold text-slate-900" data-id="element-461">
                                {Math.round(avgApps)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center" data-id="element-462">
                              <span className="text-sm text-slate-600" data-id="element-463">
                                Avg Efficiency
                              </span>
                              <span className="font-bold text-emerald-600" data-id="element-464">
                                {avgEfficiency.toFixed(1)}%
                              </span>
                            </div>
                            <div className="pt-2 border-t border-indigo-200" data-id="element-465">
                              <span className="text-xs text-slate-500" data-id="element-466">
                                {teamLos.length} Loan Officers
                              </span>
                            </div>
                          </div>
                        </motion.div>;
                })}
                  </div>
                </Card>

                {/* Performance Outliers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-467">
                  {/* High Performers */}
                  <Card data-id="element-468">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-469">
                      <span className="text-emerald-600" data-id="element-470">⭐</span>
                      High Efficiency Stars
                    </h3>
                    <p className="text-sm text-slate-500 mb-4" data-id="element-471">
                      LOs with above-average efficiency (conversion rate)
                    </p>
                    <div className="space-y-3" data-id="element-472">
                      {loPerformanceData.filter(lo => parseFloat(lo.efficiency) > 3.5).sort((a, b) => parseFloat(b.efficiency) - parseFloat(a.efficiency)).slice(0, 5).map((lo, idx) => <div key={lo.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-473">
                            <div data-id="element-474">
                              <div className="font-medium text-slate-900" data-id="element-475">
                                {lo.name}
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-476">
                                {lo.team}
                              </div>
                            </div>
                            <div className="text-right" data-id="element-477">
                              <div className="font-bold text-emerald-600" data-id="element-478">
                                {lo.efficiency}%
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-479">
                                {lo.applications} apps
                              </div>
                            </div>
                          </div>)}
                    </div>
                  </Card>

                  {/* Need Coaching */}
                  <Card data-id="element-480">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-481">
                      <span className="text-amber-600" data-id="element-482">⚠️</span>
                      Coaching Opportunities
                    </h3>
                    <p className="text-sm text-slate-500 mb-4" data-id="element-483">
                      LOs with high effort but below-average conversion
                    </p>
                    <div className="space-y-3" data-id="element-484">
                      {loPerformanceData.filter(lo => lo.totalCalls > 800 && parseFloat(lo.efficiency) < 2.5).sort((a, b) => b.totalCalls - a.totalCalls).slice(0, 5).map((lo, idx) => <div key={lo.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-485">
                            <div data-id="element-486">
                              <div className="font-medium text-slate-900" data-id="element-487">
                                {lo.name}
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-488">
                                {lo.team}
                              </div>
                            </div>
                            <div className="text-right" data-id="element-489">
                              <div className="font-bold text-amber-600" data-id="element-490">
                                {lo.totalCalls} calls
                              </div>
                              <div className="text-xs text-slate-600" data-id="element-491">
                                {lo.efficiency}% efficiency
                              </div>
                            </div>
                          </div>)}
                      {loPerformanceData.filter(lo => lo.totalCalls > 800 && parseFloat(lo.efficiency) < 2.5).length === 0 && <div className="text-center py-6 text-slate-400" data-id="element-492">
                          <p className="text-sm" data-id="element-493">
                            No coaching opportunities identified
                          </p>
                          <p className="text-xs mt-1" data-id="element-494">
                            All high-effort LOs are performing well
                          </p>
                        </div>}
                    </div>
                  </Card>
                </div>
              </div>}

            {activeTab === 'insights' && <div className="space-y-6" data-id="element-495">
                {/* Critical Alerts */}
                {insights.filter(i => i.type === 'critical').length > 0 && <Card className="border-l-4 border-red-500 bg-red-50" data-id="element-496">
                    <div className="flex items-start gap-4" data-id="element-497">
                      <div className="flex-shrink-0" data-id="element-498">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-499">
                          <AlertCircle className="w-6 h-6 text-red-600" data-id="element-500" />
                        </div>
                      </div>
                      <div className="flex-1" data-id="element-501">
                        <h3 className="text-lg font-semibold text-red-900 mb-2" data-id="element-502">
                          Critical Alerts Requiring Immediate Attention
                        </h3>
                        <div className="space-y-3" data-id="element-503">
                          {insights.filter(i => i.type === 'critical').map(insight => <div key={insight.id} className="bg-white p-4 rounded-lg border border-red-200" data-id="element-504">
                                <h4 className="font-semibold text-slate-900 mb-1" data-id="element-505">
                                  {insight.title}
                                </h4>
                                <p className="text-sm text-slate-600" data-id="element-506">
                                  {insight.description}
                                </p>
                                {insight.metric && <div className="mt-2 inline-block px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded" data-id="element-507">
                                    {insight.metric}
                                  </div>}
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </Card>}

                {/* Insights Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-508">
                  {/* Behavioral Patterns */}
                  <Card data-id="element-509">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-510">
                      <span className="text-indigo-600" data-id="element-511">🔍</span>
                      Behavioral Patterns Detected
                    </h3>
                    <div className="space-y-4" data-id="element-512">
                      {/* High Effort / Low Production */}
                      {(() => {
                    const avgEfficiency = loPerformanceData.reduce((sum, lo) => sum + parseFloat(lo.efficiency), 0) / loPerformanceData.length;
                    const highEffortLowProd = loPerformanceData.filter(lo => lo.totalCalls > 800 && parseFloat(lo.efficiency) < avgEfficiency * 0.7);
                    if (highEffortLowProd.length > 0) {
                      return <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-id="element-513">
                              <div className="flex items-start gap-3" data-id="element-514">
                                <span className="text-2xl" data-id="element-515">⚠️</span>
                                <div className="flex-1" data-id="element-516">
                                  <h4 className="font-semibold text-slate-900 mb-1" data-id="element-517">
                                    High Effort, Low Conversion
                                  </h4>
                                  <p className="text-sm text-slate-600 mb-2" data-id="element-518">
                                    {highEffortLowProd.length} LO
                                    {highEffortLowProd.length > 1 ? 's are' : ' is'}{' '}
                                    making high call volumes but converting
                                    below average.
                                  </p>
                                  <div className="space-y-1" data-id="element-519">
                                    {highEffortLowProd.slice(0, 3).map(lo => <div key={lo.id} className="text-sm" data-id="element-520">
                                        <span className="font-medium text-slate-900" data-id="element-521">
                                          {lo.name}
                                        </span>
                                        <span className="text-slate-600" data-id="element-522">
                                          {' '}
                                          - {lo.totalCalls} calls,{' '}
                                          {lo.efficiency}% conv
                                        </span>
                                      </div>)}
                                  </div>
                                  <div className="mt-3 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded inline-block" data-id="element-523">
                                    Recommendation: Review call scripts and
                                    objection handling
                                  </div>
                                </div>
                              </div>
                            </div>;
                    }
                    return null;
                  })()}

                      {/* Low Effort / High Production */}
                      {(() => {
                    const avgEfficiency = loPerformanceData.reduce((sum, lo) => sum + parseFloat(lo.efficiency), 0) / loPerformanceData.length;
                    const lowEffortHighProd = loPerformanceData.filter(lo => lo.totalCalls < 700 && parseFloat(lo.efficiency) > avgEfficiency * 1.3);
                    if (lowEffortHighProd.length > 0) {
                      return <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200" data-id="element-524">
                              <div className="flex items-start gap-3" data-id="element-525">
                                <span className="text-2xl" data-id="element-526">⭐</span>
                                <div className="flex-1" data-id="element-527">
                                  <h4 className="font-semibold text-slate-900 mb-1" data-id="element-528">
                                    High Efficiency Champions
                                  </h4>
                                  <p className="text-sm text-slate-600 mb-2" data-id="element-529">
                                    {lowEffortHighProd.length} LO
                                    {lowEffortHighProd.length > 1 ? 's are' : ' is'}{' '}
                                    achieving above-average results with lower
                                    call volumes.
                                  </p>
                                  <div className="space-y-1" data-id="element-530">
                                    {lowEffortHighProd.slice(0, 3).map(lo => <div key={lo.id} className="text-sm" data-id="element-531">
                                        <span className="font-medium text-slate-900" data-id="element-532">
                                          {lo.name}
                                        </span>
                                        <span className="text-slate-600" data-id="element-533">
                                          {' '}
                                          - {lo.totalCalls} calls,{' '}
                                          {lo.efficiency}% conv
                                        </span>
                                      </div>)}
                                  </div>
                                  <div className="mt-3 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded inline-block" data-id="element-534">
                                    Recommendation: Study their approach for
                                    best practices
                                  </div>
                                </div>
                              </div>
                            </div>;
                    }
                    return null;
                  })()}

                      {/* Consistent Performers */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-535">
                        <div className="flex items-start gap-3" data-id="element-536">
                          <span className="text-2xl" data-id="element-537">📊</span>
                          <div className="flex-1" data-id="element-538">
                            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-539">
                              Consistent Performance
                            </h4>
                            <p className="text-sm text-slate-600 mb-2" data-id="element-540">
                              {loPerformanceData.filter(lo => parseFloat(lo.efficiency) >= 2.5 && parseFloat(lo.efficiency) <= 4.0).length}{' '}
                              LOs maintaining steady conversion rates between
                              2.5-4.0%.
                            </p>
                            <div className="mt-3 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block" data-id="element-541">
                              Status: Healthy baseline performance
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Predictive Analytics */}
                  <Card data-id="element-542">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-543">
                      <span className="text-indigo-600" data-id="element-544">🔮</span>
                      Predictive Analytics
                    </h3>
                    <div className="space-y-4" data-id="element-545">
                      {/* Forecasted Production */}
                      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-546">
                        <h4 className="font-semibold text-slate-900 mb-3" data-id="element-547">
                          7-Day Production Forecast
                        </h4>
                        <div className="space-y-3" data-id="element-548">
                          <div data-id="element-549">
                            <div className="flex justify-between items-center mb-1" data-id="element-550">
                              <span className="text-sm text-slate-600" data-id="element-551">
                                Projected Applications
                              </span>
                              <span className="text-lg font-bold text-indigo-600" data-id="element-552">
                                {Math.round(aggregated.production.applications * 0.23)}
                              </span>
                            </div>
                            <div className="h-2 bg-indigo-100 rounded-full overflow-hidden" data-id="element-553">
                              <div className="h-full bg-indigo-500 rounded-full" style={{
                            width: '85%'
                          }} data-id="element-554"></div>
                            </div>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-555">
                              Based on current effort trends (85% confidence)
                            </p>
                          </div>

                          <div data-id="element-556">
                            <div className="flex justify-between items-center mb-1" data-id="element-557">
                              <span className="text-sm text-slate-600" data-id="element-558">
                                Projected Locks
                              </span>
                              <span className="text-lg font-bold text-indigo-600" data-id="element-559">
                                {Math.round(aggregated.production.locks * 0.23)}
                              </span>
                            </div>
                            <div className="h-2 bg-indigo-100 rounded-full overflow-hidden" data-id="element-560">
                              <div className="h-full bg-indigo-500 rounded-full" style={{
                            width: '78%'
                          }} data-id="element-561"></div>
                            </div>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-562">
                              Based on current conversion rates (78% confidence)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Trend Direction */}
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-563">
                        <h4 className="font-semibold text-slate-900 mb-3" data-id="element-564">
                          Trend Direction Indicators
                        </h4>
                        <div className="space-y-2" data-id="element-565">
                          <div className="flex items-center justify-between" data-id="element-566">
                            <span className="text-sm text-slate-600" data-id="element-567">
                              Call Volume Trend
                            </span>
                            <div className="flex items-center gap-2" data-id="element-568">
                              <span className="text-emerald-600 font-medium" data-id="element-569">
                                ↗ Increasing
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-570">
                            <span className="text-sm text-slate-600" data-id="element-571">
                              Conversion Rate Trend
                            </span>
                            <div className="flex items-center gap-2" data-id="element-572">
                              <span className="text-slate-600 font-medium" data-id="element-573">
                                → Stable
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-574">
                            <span className="text-sm text-slate-600" data-id="element-575">
                              Production Velocity
                            </span>
                            <div className="flex items-center gap-2" data-id="element-576">
                              <span className="text-emerald-600 font-medium" data-id="element-577">
                                ↗ Accelerating
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Risk Assessment */}
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-578">
                        <h4 className="font-semibold text-slate-900 mb-2" data-id="element-579">
                          Risk Assessment
                        </h4>
                        <div className="flex items-center gap-2 mb-2" data-id="element-580">
                          <div className="flex-1 h-2 bg-green-200 rounded-full overflow-hidden" data-id="element-581">
                            <div className="h-full bg-green-500 rounded-full" style={{
                          width: '85%'
                        }} data-id="element-582"></div>
                          </div>
                          <span className="text-sm font-bold text-green-700" data-id="element-583">
                            Low Risk
                          </span>
                        </div>
                        <p className="text-xs text-slate-600" data-id="element-584">
                          Current effort levels and conversion rates suggest
                          stable performance trajectory. No major risks
                          detected.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Recommendations */}
                <Card data-id="element-585">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-586">
                    <span className="text-indigo-600" data-id="element-587">💡</span>
                    Actionable Recommendations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-588">
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-100" data-id="element-589">
                      <div className="text-2xl mb-2" data-id="element-590">🎯</div>
                      <h4 className="font-semibold text-slate-900 mb-2" data-id="element-591">
                        Optimize Call Timing
                      </h4>
                      <p className="text-sm text-slate-600 mb-3" data-id="element-592">
                        Analysis shows Tuesday-Thursday 10am-2pm has highest
                        conversion rates.
                      </p>
                      <div className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-1 rounded inline-block" data-id="element-593">
                        Impact: +15% conversion potential
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-100" data-id="element-594">
                      <div className="text-2xl mb-2" data-id="element-595">📚</div>
                      <h4 className="font-semibold text-slate-900 mb-2" data-id="element-596">
                        Share Best Practices
                      </h4>
                      <p className="text-sm text-slate-600 mb-3" data-id="element-597">
                        Top performers use specific objection handling
                        techniques. Schedule knowledge sharing session.
                      </p>
                      <div className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded inline-block" data-id="element-598">
                        Impact: Team-wide improvement
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg border border-amber-100" data-id="element-599">
                      <div className="text-2xl mb-2" data-id="element-600">⚡</div>
                      <h4 className="font-semibold text-slate-900 mb-2" data-id="element-601">
                        Focus on Quality
                      </h4>
                      <p className="text-sm text-slate-600 mb-3" data-id="element-602">
                        Some LOs prioritize volume over quality. Shift focus to
                        qualified leads for better conversion.
                      </p>
                      <div className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded inline-block" data-id="element-603">
                        Impact: +20% efficiency gain
                      </div>
                    </div>
                  </div>
                </Card>

                {/* All Insights Panel */}
                <InsightPanel insights={insights} data-id="element-604" />
              </div>}

            {(activeTab === 'performance' || activeTab === 'insights') && <div className="flex items-center justify-center h-96 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl" data-id="element-605">
                <div className="text-center text-slate-400" data-id="element-606">
                  <p className="text-lg font-medium" data-id="element-607">
                    Detailed {activeTab} view under construction
                  </p>
                  <p className="text-sm" data-id="element-608">
                    Switch to Overview for full demo functionality
                  </p>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>;
}